import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function timeAgo(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return `${seconds} second${seconds !== 1 ? "s" : ""} ago`;
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  if (days < 7) return `${days} day${days !== 1 ? "s" : ""} ago`;

  // Format as dd/mm/yy if more than 7 days
  const d = date.getDate().toString().padStart(2, "0");
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const y = date.getFullYear().toString().slice(-2);

  return `${d}/${m}/${y}`;
}

export function formatCurrency(
  value: number,
  options?: {
    currencySymbol?: string; // como '$'
    showDecimals?: boolean;
  },
): string {
  const { currencySymbol = "", showDecimals = false } = options || {};

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  });

  return `${currencySymbol}${formatter.format(value)}`;
}

export function getUiStepIndex(rawStep: number, rawSteps: number[]): number {
  const idx = rawSteps.indexOf(rawStep);
  return idx >= 0 ? idx : 0;
}

export function formatDateForBackend(
  date: Date | string | null | undefined,
  options?: {
    format?: "iso" | "date" | "datetime"; // iso: 2024-01-15T00:00:00.000Z, date: 2024-01-15, datetime: 2024-01-15T10:30:00
    timezone?: "local" | "utc";
  },
): string | null {
  const { format = "iso", timezone = "utc" } = options || {};

  if (!date) return null;

  let dateObj: Date;

  if (typeof date === "string") {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  if (isNaN(dateObj.getTime())) {
    return null;
  }

  switch (format) {
    case "date":
      // Format: YYYY-MM-DD
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;

    case "datetime":
      // Format: YYYY-MM-DDTHH:mm:ss
      const dateStr = formatDateForBackend(dateObj, { format: "date" });
      const hours = String(dateObj.getHours()).padStart(2, "0");
      const minutes = String(dateObj.getMinutes()).padStart(2, "0");
      const seconds = String(dateObj.getSeconds()).padStart(2, "0");
      return `${dateStr}T${hours}:${minutes}:${seconds}`;

    case "iso":
    default:
      // Format ISO complete
      return timezone === "utc"
        ? dateObj.toISOString()
        : dateObj.toLocaleString("sv-SE", { timeZoneName: "short" });
  }
}

/**
 * Convierte una fecha de string a Date object
 * Útil para convertir fechas del backend a objetos Date
 */
export function parseDateFromBackend(
  dateString: string | null | undefined,
): Date | null {
  if (!dateString) return null;

  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Formatea una fecha para mostrar en la UI
 */
export function formatDateForUI(
  date: Date | string | null | undefined,
  options?: {
    format?: "short" | "long" | "relative";
    locale?: string;
  },
): string {
  const { format = "short", locale = "en-US" } = options || {};

  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return "";

  switch (format) {
    case "long":
      return dateObj.toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

    case "relative":
      return timeAgo(dateObj.toISOString());

    case "short":
    default:
      return dateObj.toLocaleDateString(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
  }
}

/**
 * Obtiene el locale desde las cookies del browser
 */
export function getLocaleFromCookie(): string {
  if (typeof document === "undefined") return "en";
  const match = document.cookie.match(/NEXT_LOCALE=([^;]+)/);
  return match?.[1] || "en";
}
